import { Modal as AModal } from 'antd';
import Button from 'components/Button/Button';
import React, { ReactNode } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import styled from 'styled-components';
import { Order, OrderStatus } from 'types/schema';
import utilMessages from 'utils/messages';
import { getPermissionsActive } from 'utils/permissionMap';
import messages from '../OrdersPage/messages';

const Header = styled.h1`
  color: #343538;
  font-weight: 600;
  font-size: 24px;
  line-height: 24px;
  margin: 0;
`;

const Container = styled.div`
  position: relative;
  display: flex;
  margin: 37px 0 20px;
  justify-content: space-between;
  align-items: center;
  @media (max-width: 767px) {
    padding: 0 10pt;
    margin: 15px 0 20px;
  }

  @media (max-width: 576px) {
    flex-direction: column;
    gap: 16px;
    align-items: flex-start;
  }
`;

const ButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ButtonText = styled.span`
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #343538;
`;

const CancelButtonText = styled(ButtonText)`
  color: #e2483c;
`;

const ActionButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 100px;
  height: 36px;
`;

const RateButton = styled(ActionButton)`
  background-color: #43a047;

  &:hover,
  &:focus,
  &:active {
    background-color: #43a047 !important;
  }
`;

const RateButtonText = styled(ButtonText)`
  color: #fff;
`;

interface OrderDetailPageHeaderProps {
  onPrintOrderInvoice: (orderId: string) => void;
  onReOrderAll: (data: { storeId: string; orderId: string }) => void;
  onCancelOrder: (value: any) => void;
  asComponent?: any;
  semibold?: boolean;
  children?: ReactNode;
  order: Order;
  permissions: string[];
  intl: any;
  onApproveOrder: (value: any) => void;
  onDisapproveOrder: (value: any) => void;
  onEditOrder: () => void;
}

const componentPermissions = {
  addItemToCart: 'ORDER_PLACE',
  editPriceOrder: 'ORDER_ITEM_SET_PRICE',
  editQuantityOrder: 'ORDER_ITEM_SET_QUANTITY',
  confirmDeliveryOrder: 'ORDER_DELIVERY_CONFIRM',
  cancelOrder: 'ORDER_CANCEL',
};

const OrderDetailPageHeader = (props: OrderDetailPageHeaderProps) => {
  const {
    asComponent,
    semibold,
    children,
    onPrintOrderInvoice,
    onReOrderAll,
    onCancelOrder,
    order,
    permissions,
    intl,
    onApproveOrder,
    onDisapproveOrder,
    onEditOrder,
    ...delegated
  } = props;

  const canApproveOrder = getPermissionsActive(['ORDER_APPROVE'], permissions);

  const handleCancelOrder = () => {
    const cancelOrderMessage = intl.formatMessage(messages.cancelOrderMessage);
    const yes = intl.formatMessage(utilMessages.yes);
    const no = intl.formatMessage(utilMessages.no);
    AModal.confirm({
      autoFocusButton: null,
      okButtonProps: {
        danger: true,
      },
      title: cancelOrderMessage,
      okText: yes,
      cancelText: no,
      onOk: () => {
        const { order } = props;
        onCancelOrder({
          id: order?.id,
        });
      },
    });
  };

  return (
    <Container {...delegated}>
      <Header semibold={semibold} as={asComponent}>
        {children}
      </Header>

      <ButtonWrapper>
        {order?.status === OrderStatus.PendingApproval && canApproveOrder && !order.discount && (
          <ActionButton onClick={onEditOrder}>
            <ButtonText>
              <FormattedMessage {...utilMessages.edit} />
            </ButtonText>
          </ActionButton>
        )}

        {order.status === OrderStatus.PendingApproval && canApproveOrder && (
          <>
            <ActionButton onClick={() => onDisapproveOrder({ orderId: order.id })}>
              <ButtonText>
                <FormattedMessage {...messages.decline} />
              </ButtonText>
            </ActionButton>

            <RateButton onClick={() => onApproveOrder({ orderId: order.id })}>
              <RateButtonText>
                <FormattedMessage {...utilMessages.approve} />
              </RateButtonText>
            </RateButton>
          </>
        )}

        {!OrderStatus.PendingApproval.includes(order.status) ? (
          <ActionButton
            onClick={() =>
              onReOrderAll({
                storeId: order?.store?.id,
                orderId: order?.id,
              })
            }
          >
            <ButtonText>
              <FormattedMessage {...messages.reOrder} />
            </ButtonText>
          </ActionButton>
        ) : null}

        {permissions.includes(componentPermissions.cancelOrder) &&
        [OrderStatus.Sent, OrderStatus.PendingAcceptance].includes(order.status) &&
        !order.fulfillmentNote ? (
          <ActionButton onClick={handleCancelOrder}>
            <CancelButtonText>
              <FormattedMessage {...messages.cancelOrder} />
            </CancelButtonText>
          </ActionButton>
        ) : null}
      </ButtonWrapper>
    </Container>
  );
};

export default injectIntl(OrderDetailPageHeader);
